import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { authClient } from '../lib/auth-client'
import { t } from '../lib/i18n'
import { useTRPC } from '../lib/trpc'

export const Route = createFileRoute('/login')({ component: Login })

type Mode = 'entrar' | 'criar'

/** Cabeçalho que o servidor exige para liberar cadastro — ver `lib/auth.ts`. */
const INVITE_HEADER = 'x-tipscore-invite'

function Login() {
  const trpc = useTRPC()

  // Token guardado por `/convite/:token` antes de mandar para cá. É ele que
  // decide se cadastrar é sequer uma opção nesta tela.
  const [convite] = useState(() => sessionStorage.getItem('tipscore:convite'))
  const firstRun = useQuery(trpc.firstRun.queryOptions()).data?.firstRun ?? false
  const podeCriar = Boolean(convite) || firstRun

  const [mode, setMode] = useState<Mode>('entrar')

  // Ajuste durante a render em vez de efeito: quando `firstRun` chega, a tela
  // já nasce no modo certo, sem piscar "Entrar" antes.
  const [modoResolvido, setModoResolvido] = useState(false)
  if (!modoResolvido && podeCriar) {
    setModoResolvido(true)
    setMode('criar')
  }
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const result =
      mode === 'entrar'
        ? await authClient.signIn.email({ email, password })
        : await authClient.signUp.email(
            { email, password, name },
            // Sem convite o servidor recusa; mandar o token é o que autoriza.
            { headers: convite ? { [INVITE_HEADER]: convite } : undefined },
          )

    setBusy(false)

    if (result.error) {
      // A mensagem do BetterAuth vem em inglês; traduzir os casos comuns evita
      // expor jargão de biblioteca para quem só quer entrar.
      setError(traduzirErro(result.error.message ?? '', mode))
      return
    }

    /**
     * Navegação dura, e não do roteador.
     *
     * O hook de sessão do BetterAuth guarda o resultado anterior: navegando
     * por dentro do SPA, a tela seguinte ainda lia "sem sessão" e devolvia
     * para cá — era por isso que às vezes só funcionava no segundo envio.
     * Recarregar a página garante que a sessão recém-criada seja a primeira
     * coisa lida.
     */
    if (convite) {
      // Quem chegou por um link de convite volta para ele: o aceite é
      // automático de lá, e o inbox ainda não existe para essa pessoa.
      sessionStorage.removeItem('tipscore:convite')
      window.location.assign(`/convite/${convite}`)
      return
    }

    window.location.assign('/inbox')
  }

  return (
    <main className="grid h-full place-items-center bg-paper px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center">
          <img
            src="/tipscore-icon.png"
            alt=""
            width={56}
            height={56}
            className="size-14 rounded-2xl shadow-pop"
          />
          <p className="mt-4 text-xl font-extrabold uppercase leading-none tracking-wide text-ink">
            Tip Score
          </p>
          <p className="mt-1.5 text-xs text-ink-3">{t('CRM para Telegram')}</p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-xl border border-line bg-surface p-6 shadow-card"
        >
          {convite && (
            <div className="mb-5 flex flex-col gap-1 rounded-lg border border-accent-soft-line bg-accent-soft px-3 py-2.5">
              <span className="text-2xs font-bold uppercase tracking-wider text-accent-ink">
                {t('Convite')}
              </span>
              <span className="text-xs leading-relaxed text-ink-2">
                {t('Crie sua conta para entrar no workspace. Já tem conta?')}{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === 'criar' ? 'entrar' : 'criar')
                    setError(null)
                  }}
                  className="font-semibold text-accent-ink hover:underline"
                >
                  {mode === 'criar' ? t('Entrar com ela') : t('Criar uma nova')}
                </button>
              </span>
            </div>
          )}

          {!convite && firstRun && (
            <div className="mb-5 flex flex-col gap-1 rounded-lg border border-accent-soft-line bg-accent-soft px-3 py-2.5">
              <span className="text-2xs font-bold uppercase tracking-wider text-accent-ink">
                {t('Primeiro acesso')}
              </span>
              <span className="text-xs leading-relaxed text-ink-2">
                {t(
                  'Ninguém entrou aqui ainda. Esta primeira conta vira a dona do workspace e é quem convida o resto da equipe.',
                )}
              </span>
            </div>
          )}

          {mode === 'criar' && (
            <Field label={t('Nome')}>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                className="input"
              />
            </Field>
          )}

          <Field label={t('E-mail')}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input"
            />
          </Field>

          <Field
            label={t('Senha')}
            hint={mode === 'criar' ? t('Mínimo de 10 caracteres') : undefined}
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === 'criar' ? 10 : undefined}
              autoComplete={mode === 'criar' ? 'new-password' : 'current-password'}
              className="input"
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-accent px-4 py-2.5 text-sm font-bold text-on-accent transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy ? t('Aguarde…') : mode === 'entrar' ? t('Entrar') : t('Criar conta')}
          </button>
        </form>

        {!podeCriar && (
          // Fora do cartão: é nota de rodapé sobre como se consegue acesso,
          // não instrução para preencher o formulário. Sem ela, a ausência do
          // "criar conta" parece defeito da tela.
          <p className="mt-4 text-center text-xs leading-relaxed text-ink-3">
            {t(
              'O acesso é por convite. Se ainda não tem conta, peça um link a quem administra o workspace.',
            )}
          </p>
        )}
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 8px;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          padding: 8px 10px;
          font-size: 13px;
          color: var(--ink);
        }
        .input:focus { border-color: var(--accent-ink); }
        /* 16px no celular: com menos que isso o Safari amplia a tela ao focar
           e não volta. A regra global não alcança esta folha embutida. */
        @media (pointer: coarse) { .input { font-size: 16px; } }
      `}</style>
    </main>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-2xs font-bold uppercase tracking-wider text-ink-3">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-2xs text-ink-3">{hint}</span>}
    </label>
  )
}

function traduzirErro(message: string, mode: Mode): string {
  const lower = message.toLowerCase()
  if (lower.includes('invalid email or password') || lower.includes('invalid credentials')) {
    return t('E-mail ou senha incorretos')
  }
  if (lower.includes('already exists') || lower.includes('existing')) {
    return t('Já existe uma conta com este e-mail')
  }
  if (lower.includes('password') && lower.includes('short')) {
    return t('A senha precisa ter ao menos 10 caracteres')
  }
  return (
    message ||
    (mode === 'entrar' ? t('Não foi possível entrar') : t('Não foi possível criar a conta'))
  )
}
